import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { codeToSlug } from '../data/products';
import GoldRule from '../components/GoldRule';
import ImageGallery from '../components/ImageGallery';
import { FeatureBadge } from '../components/FeatureBadge';
import useProductImages, { cleanImage } from '../hooks/useProductImages';
import useProducts from '../hooks/useProducts';
import ImageMarquee from '../components/motion/ImageMarquee';
import { Reveal, RevealLines, Stagger, StaggerItem } from '../components/motion/Reveal';

const productSets = (code, allSets) =>
  allSets.filter((s) => s.items.includes(code));

const BASE_CATEGORIES = ['Wallets', 'Business Bags', 'Pens', 'Notebooks', 'Portfolios', 'Travel', 'Gift Sets', 'Accessories'];

export default function Products() {
  const [cat, setCat] = useState('All');
  const { manifest } = useProductImages();
  const { visibleProducts, sets, isCustom } = useProducts();

  const allProducts = useMemo(() => Object.values(visibleProducts), [visibleProducts]);
  const filtered = cat === 'All'
    ? allProducts
    : allProducts.filter((p) => p.category === cat);

  const marqueeItems = useMemo(
    () =>
      allProducts
        .map((p) => ({ code: p.code, name: p.name, image: cleanImage(manifest, p.code) }))
        .filter((p) => p.image)
        .slice(0, 12),
    [allProducts, manifest]
  );

  // Include any category introduced by custom products added in the atelier.
  const CATEGORIES = useMemo(() => {
    const extra = allProducts
      .map((p) => p.category)
      .filter((c) => c && !BASE_CATEGORIES.includes(c));
    return ['All', ...BASE_CATEGORIES, ...Array.from(new Set(extra))];
  }, [allProducts]);

  return (
    <div data-testid="products-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
        <Reveal y={12}>
          <div className="eyebrow text-mb-muted mb-8">The catalogue</div>
        </Reveal>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-mb leading-[0.95]">
          <RevealLines lines={['Every piece,']} />
          <RevealLines lines={['one by one.']} delay={0.1} lineClassName="italic" />
        </h1>
        <Reveal delay={0.3} className="mt-10">
          <GoldRule size="med" />
        </Reveal>
        <Reveal delay={0.36}>
          <p className="mt-10 font-display italic text-[18px] md:text-[20px] text-mb-muted max-w-2xl leading-relaxed">
            The individual leathers that make up the house sets — same hides,
            same stitch, available on their own terms.
          </p>
        </Reveal>
      </section>

      {/* Drifting catalogue strip */}
      <section className="pb-14" data-testid="products-marquee">
        <ImageMarquee items={marqueeItems} height={170} baseSpeed={20} />
      </section>

      {/* Filter */}
      <section className="hairline-t hairline-b border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center gap-8 overflow-x-auto whitespace-nowrap">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`products-filter-${c.toLowerCase().replace(/\s/g, '-')}`}
              className={`eyebrow transition-colors ${
                c === cat ? 'text-gold' : 'text-mb-muted hover:text-mb'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <Stagger
          key={cat}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          gap={0.06}
        >
          {filtered.map((p) => {
            const inSets = productSets(p.code, sets);
            const images = (manifest[p.code] || []).filter((u) => u.includes('-clean'));
            const imgCount = images.length;
            return (
              <StaggerItem
                key={p.code}
                y={24}
                className="bg-mb-surface hairline border-subtle p-6 hover:border-gold transition-colors group relative"
                data-testid={`product-card-${p.code.replace(/\s/g, '-')}`}
              >
                {isCustom(p.code) && (
                  <span className="absolute top-3 right-3 z-10 feature-badge feature-badge--muted" title="Custom-added">
                    NEW
                  </span>
                )}
                <Link to={`/products/${codeToSlug(p.code)}`} aria-label={`View ${p.name}`}>
                  <ImageGallery
                    code={p.code}
                    name={p.name}
                    images={images.slice(0, 1) /* single preview in grid */}
                    mainRatio="aspect-[4/5]"
                  />
                </Link>

                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <div className="eyebrow-sm text-gold">{p.code}</div>
                    <div className="eyebrow-sm text-mb-muted">{p.category}</div>
                  </div>
                  <Link
                    to={`/products/${codeToSlug(p.code)}`}
                    className="mt-3 block font-display text-[22px] text-mb group-hover:text-gold transition-colors"
                  >
                    {p.name}
                  </Link>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <div className="eyebrow-sm text-mb-muted">Size</div>
                      <div className="mt-1 font-display text-[14px] text-mb">{p.dimensions}</div>
                    </div>
                    <div>
                      <div className="eyebrow-sm text-mb-muted">Leather</div>
                      <div className="mt-1 font-display text-[14px] text-mb">{p.leather}</div>
                    </div>
                  </div>

                  {/* Color swatches row */}
                  {p.colors?.length > 0 && (
                    <div className="mt-4 flex items-center gap-2">
                      {p.colors.slice(0, 5).map((c, ci) => (
                        <span
                          key={c.ref || `${c.label}-${ci}`}
                          className="w-4 h-4 hairline border-gold"
                          style={{ background: c.hex }}
                          title={c.ref ? `${c.label} (${c.ref})` : c.label}
                        />
                      ))}
                      <span className="eyebrow-sm text-mb-muted ml-2">
                        {p.colors.length === 1 ? '1 finish' : `${p.colors.length} finishes`}
                      </span>
                    </div>
                  )}

                  {p.features?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.features.slice(0, 3).map((f, i) => (
                        <FeatureBadge key={`${p.code}-f-${i}`} muted>
                          {f}
                        </FeatureBadge>
                      ))}
                    </div>
                  )}

                  {inSets.length > 0 && (
                    <div className="mt-6 hairline-t border-subtle pt-4">
                      <div className="eyebrow-sm text-mb-muted mb-3">Featured in</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {inSets.map((s) => (
                          <Link
                            key={`${p.code}-${s.slug}`}
                            to={`/sets/${s.slug}`}
                            className="eyebrow text-mb hover:text-gold transition-colors"
                            data-testid={`product-in-set-${p.code.replace(/\s/g, '-')}-${s.slug}`}
                          >
                            {s.name} →
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-6">
                    <Link
                      to={`/products/${codeToSlug(p.code)}`}
                      className="btn-outline-gold w-full"
                      data-testid={`product-view-${codeToSlug(p.code)}`}
                    >
                      View piece{imgCount > 1 ? ` · ${imgCount} images` : ''}
                    </Link>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section className="hairline-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal y={12}>
            <div className="eyebrow text-mb-muted mb-6">In quantity</div>
          </Reveal>
          <h2 className="font-display italic text-3xl md:text-5xl text-mb">
            <RevealLines
              lines={['We prefer to present our work', 'in sets.']}
              className="text-center"
            />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-[14px] text-mb-muted max-w-lg mx-auto">
              Individual pieces are available by enquiry. Sets are how we intend them to be used.
            </p>
          </Reveal>
          <Reveal delay={0.28} className="mt-10">
            <Link
              to="/sets"
              className="btn-gold"
              data-magnetic
              data-testid="products-to-sets-cta"
            >
              View the four sets
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
